import IdleJs from 'idle-js';

import { logAppState, detectAppPhase } from './lifecycleUtil';
import { PHASES, initializeSocketIo } from './lifecyclePhases';

async function resetLocalStorage() {
  console.log('Clear local storage');
  try {
    localStorage.clear();
  } catch (err) {
    console.log('Failed to clear localStorage', err);
  }
}

async function resetCacheStorage() {
  console.log('Delete all application caches');

  try {
    if (caches) {
      let keys = await caches.keys();
      keys.forEach(function (key, index, array) {
        if (key.indexOf('workbox-precache') < 0) {
          console.log('delete cache', key);
          caches.delete(key);
        }
      });
    }
  } catch (err) {
    console.warn('Delete all application caches failed. Cause:', err);
  }
}

async function resetCookies() {
  console.log('Delete all cookies');
  let cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    var cookie = cookies[i];
    var eqPos = cookie.indexOf('=');
    var name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
    document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT';
  }
}

let idle;
const state = {
  isPwaMode: process.env.VUE_APP_PWA_ENABLED === 'true', //duplicated state from root state because we need to access it in mutations
  phase: 'onStartup', // cannot use PHASES.onStartup.name because it creates an issue in lifecycleUtil.test.js

  // application phase states
  swRegistered: false, // in pwa mode: true as soon the service worker has been registered
  preCacheLoaded: false, // in pwa mode: truthy when pre-cache-manifest files have been loaded
  isDefaultLanguageLoaded: false, // true when default language has been loaded
  configsLoaded: false, // true if pre-auth configs got loaded
  authenticated: false, // user is authenticated
  isLanguageLoaded: false, // true when language has been loaded
  isModuleConfigsProcessed: false, // tenant specific configuration for example routing
};

const getters = {
  isOnStartup: (state) => state.phase === PHASES.onStartup.name,
  isBootstrapping: (state) => state.phase === PHASES.bootstrapping.name,
  isInLoginPhase: (state) => state.phase === PHASES.login.name,
  isAuthenticated: (state, getters, rootState) => state.phase === PHASES.authenticated.name,
  isReady: (state, getters, rootState) => state.phase === PHASES.ready.name,
};

const mutations = {
  setPhase: (state, { phase }) => (state.phase = phase),
};

const actions = {
  /**
   * Call this to start the app lifecycle
   */
  init: ({ dispatch }) => dispatch('setAppStates', { logMsg: 'init lifecycle' }),
  /**
   * App state may be set using this action solely. It triggers the appropriate phases.
   */
  setAppStates({ rootState, state, commit, dispatch }, payload) {
    let { authenticated, configsLoaded, swRegistered, preCacheLoaded, isModuleConfigsProcessed, isDefaultLanguageLoaded, isLanguageLoaded, logMsg } = payload;
    // console.log('setAppStates', logMsg ? logMsg : '', JSON.stringify(payload, null, 2));
    if (authenticated !== undefined) state.authenticated = authenticated;
    if (configsLoaded !== undefined) state.configsLoaded = configsLoaded;
    if (swRegistered !== undefined) state.swRegistered = swRegistered;
    if (preCacheLoaded !== undefined) state.preCacheLoaded = preCacheLoaded;
    if (isModuleConfigsProcessed !== undefined) state.isModuleConfigsProcessed = isModuleConfigsProcessed;
    if (isDefaultLanguageLoaded !== undefined) state.isDefaultLanguageLoaded = isDefaultLanguageLoaded;
    if (isLanguageLoaded !== undefined) state.isLanguageLoaded = isLanguageLoaded;
    logAppState(payload);

    let potentialNextPhase = detectAppPhase(state, state.isPwaMode, PHASES);
    console.log(`setAppStates potentialNextPhase=${potentialNextPhase}`)
    if (potentialNextPhase.name !== PHASES.unknown.name && potentialNextPhase.name !== state.phase) {
      if (potentialNextPhase.enter) {
        console.log('--------');
        console.log(`👍 Phase transition from "${state.phase}" to "${potentialNextPhase.name}"`);
        commit('setPhase', { phase: potentialNextPhase.name });
        potentialNextPhase.enter({ rootState, state, commit, dispatch });
      }
    }
  },
  stopIdleWatcher() {
    if (idle) {
      console.log('Stop watching idle states');
      idle
        .stop() // stops all tracking
        .reset(); // reset visible and idle state to initial values
      //.start();
      idle = null;
    }
    // In case stopping is needed
    // idle
    //   .stop() // stops all tracking
    //   .reset() // reset visible and idle state to initial values
    //   .start();

    // // Reset to a specific state
    // idle.reset({
    //   idle: false,
    //   visible: !document.hidden,
    // });
  },
  startIdleWatcher({ rootState, dispatch }) {
    // Configure idle handler
    if (rootState.configs.features.autoLogoutAfterInactivityInSeconds > 0) {
      console.log('Start watching idle events. Log out after ', rootState.configs.features.autoLogoutAfterInactivityInSeconds, ' Seconds');

      idle = new IdleJs({
        idle: rootState.configs.features.autoLogoutAfterInactivityInSeconds * 1000, // idle time in ms
        events: ['mousemove', 'keydown', 'mousedown', 'touchstart'], // events that will trigger the idle resetter
        onIdle: function () {
          console.log(`OnIdle after ${rootState.configs.features.autoLogoutAfterInactivityInSeconds} seconds`);
          localStorage.setItem('idleStart', new Date().toISOString());
          dispatch('profile/logout', {}, { root: true });
        },
        // callback function to be executed after idle time
        onActive: function () {
          console.log('callback function to be executed after back from idleness');
          localStorage.removeItem('idleStart');
          dispatch('messaging/registerSocketHandlers', {}, { root: true });
        },
        // callback function to be executed after back from idleness
        onHide: function () {
          console.log('callback function to be executed when window becomes hidden');
        },
        // callback function to be executed when window become hidden
        onShow: function () {
          localStorage.removeItem('idleStart');
          console.log('callback function to be executed when window becomes visible');
        }, // callback function to be executed when window become visible
        keepTracking: true, // set it to false if you want to be notified only on the first idleness change
        startAtIdle: false, // set it to true if you want to start in the idle state
      });
      idle.start();
    }
  },
  async resetCaches({ commit, dispatch }) {
    await resetLocalStorage();
    await resetCacheStorage();
  },
  /**
   * Reset app, that is localStorage, cookies and application cache. Should only be done, when being online. Otherwise the user is lost
   */
  async resetApp({ commit, dispatch }, params = {}) {
    let { windowReload } = params;
    await resetLocalStorage();

    await resetCacheStorage();

    await resetCookies();

    if (windowReload) {
      console.log('Reload app');
      window.location.reload();
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
